import { useEffect, useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { 
  BarChart3, 
  TrendingUp, 
  Calendar,
  ShoppingBag,
  Send,
  CheckCircle,
  Loader2,
  StickyNote,
  Plus,
  Trash2,
  X
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TEMPLATES = {
  'betboom': 'Betboom',
  'cashinout': 'Cashinout.io',
  'onesix': 'ONESIX',
  'bybit': 'P2P Биржа',
  'ligastavok': 'Лига Ставок',
  'platipomiru': 'Плати по миру'
};

const STICKER_COLORS = [
  { bg: 'bg-yellow-100', border: 'border-yellow-300', text: 'text-yellow-800' },
  { bg: 'bg-pink-100', border: 'border-pink-300', text: 'text-pink-800' },
  { bg: 'bg-blue-100', border: 'border-blue-300', text: 'text-blue-800' },
  { bg: 'bg-green-100', border: 'border-green-300', text: 'text-green-800' },
  { bg: 'bg-purple-100', border: 'border-purple-300', text: 'text-purple-800' },
  { bg: 'bg-orange-100', border: 'border-orange-300', text: 'text-orange-800' },
];

export default function Statistics() {
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    requestsByDay: [],
    unlockedBalance: 0,
    unlockedOzon: 0,
    totalSent: 0,
    templateStats: {}
  });
  const [stickers, setStickers] = useState([]);
  const [newStickerText, setNewStickerText] = useState('');
  const [editingSticker, setEditingSticker] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  };

  const fetchStickers = async () => {
    try {
      const response = await axios.get(`${API}/stickers`, { headers: getAuthHeaders() });
      setStickers(response.data || []);
    } catch (error) {
      // Если API не существует, используем localStorage
      const saved = localStorage.getItem('statistics_stickers');
      if (saved) setStickers(JSON.parse(saved));
    }
  };

  const saveStickersToStorage = (newStickers) => {
    localStorage.setItem('statistics_stickers', JSON.stringify(newStickers));
  };

  const addSticker = () => {
    if (!newStickerText.trim()) {
      toast.error("Введите текст заметки");
      return;
    }
    const colorIndex = stickers.length % STICKER_COLORS.length;
    const newSticker = {
      id: Date.now().toString(),
      text: newStickerText,
      color: colorIndex,
      createdAt: new Date().toISOString()
    };
    const newStickers = [...stickers, newSticker];
    setStickers(newStickers);
    saveStickersToStorage(newStickers);
    setNewStickerText('');
    toast.success("Заметка добавлена");
  };

  const updateSticker = (id, text) => {
    const newStickers = stickers.map(s => s.id === id ? { ...s, text } : s);
    setStickers(newStickers);
    saveStickersToStorage(newStickers);
    setEditingSticker(null);
    toast.success("Заметка обновлена");
  };

  const deleteSticker = (id) => {
    const newStickers = stickers.filter(s => s.id !== id);
    setStickers(newStickers);
    saveStickersToStorage(newStickers);
    toast.success("Заметка удалена");
  };

  const fetchStatistics = async () => {
    try {
      const [requestsRes, statsRes, profilesRes] = await Promise.all([
        axios.get(`${API}/requests`, { headers: getAuthHeaders() }),
        axios.get(`${API}/requests/stats`, { headers: getAuthHeaders() }),
        axios.get(`${API}/ozon/profiles`, { headers: getAuthHeaders() })
      ]);
      
      const requests = requestsRes.data || [];
      const profiles = profilesRes.data || [];
      
      // Группируем заявки по дням
      const byDay = {};
      requests.forEach(req => {
        if (req.sent_at) {
          const date = new Date(req.sent_at).toLocaleDateString('ru-RU');
          if (!byDay[date]) {
            byDay[date] = { count: 0, balance: 0 };
          }
          byDay[date].count++;
          byDay[date].balance += req.balance || 0;
        }
      });
      
      // Преобразуем в массив и сортируем по дате
      const requestsByDay = Object.entries(byDay)
        .map(([date, data]) => ({ date, ...data }))
        .sort((a, b) => {
          const [dayA, monthA, yearA] = a.date.split('.');
          const [dayB, monthB, yearB] = b.date.split('.');
          return new Date(yearB, monthB - 1, dayB) - new Date(yearA, monthA - 1, dayA);
        })
        .slice(0, 14); // Последние 14 дней
      
      // Статистика по шаблонам
      const templateStats = {};
      requests.forEach(req => {
        if (req.template && req.status === 'sent') {
          if (!templateStats[req.template]) {
            templateStats[req.template] = { count: 0, balance: 0 };
          }
          templateStats[req.template].count++;
          templateStats[req.template].balance += req.balance || 0;
        }
      });
      
      // Разблокированные OZON из профилей (is_blocked=false)
      const activeProfiles = profiles.filter(p => !p.is_blocked);
      const unlockedOzon = activeProfiles.length;
      const unlockedBalance = activeProfiles.reduce((sum, p) => sum + (p.balance || 0), 0);
      
      // Отправленные
      const sentRequests = requests.filter(r => r.status === 'sent' || r.status === 'completed');
      const totalSent = sentRequests.length;
      
      setStats({
        requestsByDay,
        unlockedBalance,
        unlockedOzon,
        totalSent,
        templateStats
      });
    } catch (error) {
      toast.error("Ошибка загрузки статистики");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStatistics();
    fetchStickers();
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const maxCount = Math.max(...stats.requestsByDay.map(d => d.count), 1);

  return (
    <div className="animate-fade-in space-y-6" data-testid="statistics-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Статистика
        </h1>
        <p className="text-slate-500">Аналитика заявок и разблокировок</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-cyan-50 to-blue-50 border-cyan-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-cyan-500 rounded-lg">
                <Send className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-cyan-600">Отправленные заявки</p>
                <p className="text-2xl font-bold text-cyan-800">{stats.totalSent}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-500 rounded-lg">
                <CheckCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-emerald-600">Разблокированный OZON</p>
                <p className="text-2xl font-bold text-emerald-800">{stats.unlockedOzon}</p>
                <p className="text-sm text-blue-600 font-semibold">{(stats.unlockedOzon * 2000).toLocaleString()} ₽</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500 rounded-lg">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-blue-600">Разблокированный баланс</p>
                <p className="text-2xl font-bold text-blue-800">{stats.unlockedBalance.toLocaleString()} ₽</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-violet-50 to-purple-50 border-violet-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-violet-500 rounded-lg">
                <ShoppingBag className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-violet-600">Средний чек</p>
                <p className="text-2xl font-bold text-violet-800">
                  {stats.unlockedOzon > 0 ? Math.round(stats.unlockedBalance / stats.unlockedOzon).toLocaleString() : 0} ₽
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Requests by Day Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-cyan-500" />
            Отправленные заявки по дням
          </CardTitle>
        </CardHeader>
        <CardContent>
          {stats.requestsByDay.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              Нет данных за последние 14 дней
            </div>
          ) : (
            <div className="space-y-3">
              {stats.requestsByDay.map((day, index) => (
                <div key={index} className="flex items-center gap-4">
                  <div className="w-24 text-sm text-slate-600 font-medium">{day.date}</div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <div 
                        className="h-8 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-md transition-all"
                        style={{ width: `${(day.count / maxCount) * 100}%`, minWidth: '20px' }}
                      />
                      <span className="text-sm font-semibold text-slate-700">{day.count}</span>
                    </div>
                  </div>
                  <div className="w-32 text-right text-sm text-slate-500">
                    {day.balance.toLocaleString()} ₽
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Template Statistics */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-violet-500" />
            Статистика по шаблонам
          </CardTitle>
        </CardHeader>
        <CardContent>
          {Object.keys(stats.templateStats).length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              Нет данных по шаблонам. Отправьте заявки с выбором шаблона.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(stats.templateStats).map(([templateId, data]) => (
                <div 
                  key={templateId} 
                  className="p-4 bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl border border-slate-200"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-slate-700">{TEMPLATES[templateId] || templateId}</span>
                    <span className="text-2xl font-bold text-violet-600">{data.count}</span>
                  </div>
                  <div className="text-sm text-slate-500">
                    Сумма: {data.balance.toLocaleString()} ₽
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Пояснения - Стикеры */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <StickyNote className="w-5 h-5 text-amber-500" />
            Пояснения
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Добавление новой заметки */}
          <div className="flex gap-3 mb-6">
            <input
              type="text"
              value={newStickerText}
              onChange={(e) => setNewStickerText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addSticker()}
              placeholder="Напишите заметку..."
              className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            />
            <Button onClick={addSticker} className="bg-amber-500 hover:bg-amber-600">
              <Plus className="w-4 h-4 mr-1" />
              Добавить
            </Button>
          </div>

          {/* Сетка стикеров */}
          {stickers.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              Нет заметок. Добавьте первую заметку выше.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {stickers.map((sticker) => {
                const color = STICKER_COLORS[sticker.color % STICKER_COLORS.length];
                return (
                  <div
                    key={sticker.id}
                    className={`relative p-4 rounded-lg border-2 ${color.bg} ${color.border} shadow-sm hover:shadow-md transition-shadow min-h-[120px]`}
                  >
                    {/* Кнопка удаления */}
                    <button
                      onClick={() => deleteSticker(sticker.id)}
                      className="absolute top-2 right-2 p-1 rounded-full hover:bg-white/50 text-slate-400 hover:text-red-500 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>

                    {/* Контент стикера */}
                    {editingSticker === sticker.id ? (
                      <textarea
                        autoFocus
                        defaultValue={sticker.text}
                        onBlur={(e) => updateSticker(sticker.id, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            updateSticker(sticker.id, e.target.value);
                          }
                          if (e.key === 'Escape') setEditingSticker(null);
                        }}
                        className={`w-full h-full min-h-[80px] bg-transparent border-none outline-none resize-none ${color.text}`}
                      />
                    ) : (
                      <div
                        onClick={() => setEditingSticker(sticker.id)}
                        className={`cursor-pointer ${color.text} whitespace-pre-wrap`}
                      >
                        {sticker.text}
                      </div>
                    )}

                    {/* Дата создания */}
                    <div className={`absolute bottom-2 left-4 text-xs opacity-60 ${color.text}`}>
                      {new Date(sticker.createdAt).toLocaleDateString('ru-RU')}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
